import React from 'react';
import { useTranslation } from 'react-i18next';
import { IProvider } from 'store/providers/providerTypes';
import styled from 'styled-components';
import LocalIcon from 'ui/LocalIcon';
import { black } from 'utils/style/colors';
import { getProviderAddress } from '../ProfileHead/ProfileHead';
import webda from '../../../../assets/images/webda.svg';
import { getDomainAndPort } from 'api/Utils';

const Footer = styled.footer`
  width: 80%;
  max-width: 1024px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2rem;

  @media screen and (max-width: 600px) {
    width: 90%;
  }
`;

const FooterHeader = styled.div`
  width: 100%;
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin: 2rem 0;
`;

const FooterLogo = styled.img`
  height: 2.5rem;
  object-fit: contain;
  object-position: center;
`;

const FooterProviderName = styled.h2`
  display: inline-block;
  flex: 1;
`;

const FooterContent = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  gap: 2rem;

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
    gap: 3rem;
  }
`;

const ContactInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContainerHeader = styled.h3`
  display: inline-block;
  width: 100%;
  margin-bottom: 1rem;
  color: var(--primary-2);
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContactRow = styled.div`
  display: flex;
  gap: 0.5rem;
  line-height: 2rem;
`;

const ContactRowInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
`;

const AboutProjectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;

const WebdaBtn = styled.a`
  width: 100%;
  border-radius: 30px;
  padding: 1.5rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;
  background: var(--foreground);
  color: ${black};
  cursor: pointer;
`;

const WebdaContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

interface IProps {
  provider: IProvider;
}

const DomainFooter: React.FC<IProps> = ({ provider }) => {
  const hasWebdaBtn =
    getDomainAndPort().domainFeatures?.enableWebdaRedirectFooterBtn;
  const { t } = useTranslation('general');

  const providerLogo = provider.documents.find(
    (it) => it.document?.documentType === 'profile_picture'
  );

  return (
    <Footer>
      <FooterHeader>
        <FooterLogo src={providerLogo?.document?.url || webda} />
        <FooterProviderName>{provider?.name}</FooterProviderName>
      </FooterHeader>
      <FooterContent>
        <ContactInfoContainer>
          <ContainerHeader>{t('ContactInfo')}</ContainerHeader>
          <RowContainer>
            <ContactRow>
              <LocalIcon iconName="phone.svg" color={black} type="sm" />
              <ContactRowInfo>
                <div>
                  {t('phone')} {' : '}
                </div>
                <div>
                  {provider.address?.phoneNumber ||
                    provider.publicProperties?.phoneNumber}
                </div>
              </ContactRowInfo>
            </ContactRow>
            <ContactRow>
              <LocalIcon iconName="email.svg" color={black} type="sm" />
              <ContactRowInfo>
                <div>
                  {t('email')}
                  {' : '}
                </div>
                <div>{provider.publicProperties?.email}</div>
              </ContactRowInfo>
            </ContactRow>
            <ContactRow>
              <LocalIcon iconName="map.svg" color={black} type="sm" />
              <ContactRowInfo>
                <div>{getProviderAddress(provider) || ''}</div>
              </ContactRowInfo>
            </ContactRow>
          </RowContainer>
        </ContactInfoContainer>
        <AboutProjectContainer>
          <ContainerHeader>{t('AboutProject')}</ContainerHeader>
          <ContactRow>{provider?.description || ''}</ContactRow>
          {hasWebdaBtn ? (
            <WebdaBtn href="https://webda.ir" target="_blank">
              <FooterLogo src={webda} />
              <WebdaContent>
                <div className="font-bold">{t('webda')}</div>
                <div>{t('healthOrganization')}</div>
              </WebdaContent>
            </WebdaBtn>
          ) : (
            <></>
          )}
        </AboutProjectContainer>
      </FooterContent>
    </Footer>
  );
};

export default DomainFooter;
